import { supabase } from './supabaseClient.js';
import { getLocalDateString } from './constants.js';

// --- READ OPERATIONS ---
/* src/storageService.js */
export const loadFullUserState = async (userId) => {
  try {
    const [userRes, goalsRes, docketRes, progressRes, quizRes, cramDecksRes, statsRes, achievementsRes] = await Promise.all([
      supabase.from('users').select('*').eq('id', userId).maybeSingle(),
      supabase.from('user_goals').select('*').eq('user_id', userId).maybeSingle(),
      supabase.from('user_docket').select('*').eq('user_id', userId).maybeSingle(),
      supabase.from('user_progress').select('*').eq('user_id', userId),
      supabase.from('user_quiz_attempts').select('*').eq('user_id', userId),
      supabase.from('user_cram_decks').select('*').eq('user_id', userId),
      supabase.from('user_stats').select('*').eq('user_id', userId).maybeSingle(),
      supabase.from('user_achievements').select('*, achievements(*)').eq('user_id', userId)
    ]);

    if (userRes.error) throw userRes.error;

    const profile = {
      name: userRes.data?.name || '',
      phone: userRes.data?.phone || '',
      display_name: userRes.data?.display_name || 'Counselor',
      zen_mode: userRes.data?.zen_mode || false,
      weekly_points: userRes.data?.weekly_points || 0,
      preferences: userRes.data?.preferences || {}
    };

    const userMeta = {
        ...(userRes.data?.meta || {}),
        current_streak_days: statsRes.data?.current_streak || 0,
        last_completed_day: statsRes.data?.last_activity_date
    };

    const goalsDB = goalsRes.data || {};
    const goals = {
      mix_mode: goalsDB.mix_mode || false,
      daily_rule_target: goalsDB.daily_target || 5,
      target_mastery_date: goalsDB.target_mastery_date,
      bar_exam_date: goalsDB.exam_date,
      study_days: goalsDB.study_days || [],
      excluded_subjects: goalsDB.excluded_subjects || [],
      denovo_track_subjects: goalsDB.denovo_track_subjects || [],
      hfr_restricted_subjects: goalsDB.hfr_restricted_subjects || []
    };

    // Hydrate diagnostics from preferences to restore subject order
    const diagnostics = {
        subject_sequence: profile.preferences.subject_sequence || [],
        is_retaker: profile.preferences.is_retaker || false,
        prep_course: profile.preferences.prep_course || {}
    };

    const rulesProgress = {};
    (progressRes.data || []).forEach(row => {
      rulesProgress[row.rule_id] = {
        status: row.status,
        srs_data: row.srs_data,
        keynote: row.keynote,
        is_flagged: row.is_flagged,
        attempt_history: []
      };
    });

    const quizzesProgress = {};
    (quizRes.data || []).forEach(attempt => {
      const attemptObj = {
        timestamp: attempt.timestamp,
        userScore: attempt.score,
        userAttempt: attempt.user_answer,
        feedback: attempt.ai_feedback,
        sourceId: attempt.quiz_id
      };
      if (rulesProgress[attempt.quiz_id]) {
        rulesProgress[attempt.quiz_id].attempt_history.push(attemptObj);
        rulesProgress[attempt.quiz_id].attempt_history.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      }
      if (!quizzesProgress[attempt.quiz_id]) {
        quizzesProgress[attempt.quiz_id] = { status: 'in_progress', high_score: 0, attempt_history: [] };
      }
      const qProg = quizzesProgress[attempt.quiz_id];
      qProg.attempt_history.push(attemptObj);
      qProg.high_score = Math.max(qProg.high_score, attempt.score || 0);
      if (qProg.high_score >= 3) qProg.status = 'mastered';
      qProg.attempt_history.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    });

    const achievements = (achievementsRes.data || []).map(row => ({
        id: row.achievement_slug,
        unlockedAt: row.unlocked_at,
        details: row.achievements
    }));

    const docket = {
        schedule: Array.isArray(docketRes.data?.schedule) ? docketRes.data.schedule : [],
        daily_metrics: docketRes.data?.daily_metrics || {}
    };

    return {
      name: profile.name,
      phone: profile.phone,
      access_level: userRes.data?.access_level || 'free',
      onboarding_status: userRes.data?.onboarding_status || 'initiated',
      profile,
      userMeta,
      goals,
      diagnostics,
      rulesProgress,
      quizzesProgress,
      docket,
      userStats: { daily_activity: [] },
      cramDecks: cramDecksRes.data || [],
      achievements
    };
  } catch (error) {
    console.error('CRITICAL: Failed to load user data', error);
    return { rulesProgress: {}, goals: {}, docket: { schedule: [] }, cramDecks: [], achievements: [] };
  }
};

export const getDashboardMetrics = async (userId) => {
    const { count, error } = await supabase
        .from('user_progress')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('status', 'mastered');
    
    if (error) return 0;
    return count;
};

export const loadCanonicalData = async () => {
  try {
    const [rulesResponse, quizzesResponse] = await Promise.all([
        supabase.from('canon_rules_secure').select('*'),
        supabase.from('canon_quizzes').select('*')
    ]);

    if (rulesResponse.error) throw rulesResponse.error;
    if (quizzesResponse.error) throw quizzesResponse.error;

    const formattedRules = rulesResponse.data.map(r => {
        let extractedHNU = r.hnu || '';
        if (!extractedHNU && r.id?.includes('_')) {
            extractedHNU = r.id.split('_')[1] || '';
        }
        return {
            ...r,
            ruleName: r.rule_name || 'Untitled Rule', 
            HNU: String(extractedHNU).trim(), 
            subject: (r.subject_slug || 'torts').toLowerCase().trim(),
            displayPath: r.display_path || [],
            isHFR: r.is_hfr
        };
    });

    const formattedQuizzes = quizzesResponse.data.map(q => {
        let related = q.related_hnu || [];
        if (typeof related === 'string') {
            try { related = JSON.parse(related); } 
            catch (e) { related = related.split(',').map(s => s.trim()); }
        }
        return {
            fact_pattern_id: q.id,
            fact_pattern: q.fact_pattern,
            answer_key: q.answer_key,
            related_HNU: Array.isArray(related) ? related : [related],
            is_free_preview: q.is_free_preview
        };
    });

    return { rules: formattedRules, quizzes: formattedQuizzes };
  } catch (error) {
    console.error('CRITICAL: Failed to load curriculum:', error);
    return { rules: [], quizzes: [] };
  }
};

export const updateUserProfile = async (userId, { name, phone, preferences, display_name, zen_mode }) => {
    const updates = {
        updated_at: new Date().toISOString()
    };
    if (name !== undefined) updates.name = name.trim();
    if (phone !== undefined) updates.phone = phone.trim();
    if (preferences !== undefined) updates.preferences = preferences;
    if (display_name !== undefined) updates.display_name = display_name.trim();
    if (zen_mode !== undefined) updates.zen_mode = zen_mode;
    
    if (Object.keys(updates).length > 1) { 
        await supabase.from('users').update(updates).eq('id', userId);
    }
};


export const saveUserMeta = async (userId, meta) => {
    // FIX: Add updated_at so iOS knows meta has changed
    const { error } = await supabase.from('users').update({ 
        meta,
        updated_at: new Date().toISOString() 
    }).eq('id', userId);
    
    if (error) {
        console.error("Error saving user meta:", error);
    }
};


export const updateUserGoals = async (userId, goals) => {
    const payload = {
        user_id: userId,
        updated_at: new Date().toISOString() // FIX: Enforce ISO string
    };
    if (goals.bar_exam_date !== undefined) payload.exam_date = goals.bar_exam_date;
    if (goals.target_mastery_date !== undefined) payload.target_mastery_date = goals.target_mastery_date;
    if (goals.daily_rule_target !== undefined) payload.daily_target = goals.daily_rule_target;
    if (goals.mix_mode !== undefined) payload.mix_mode = goals.mix_mode;
    if (goals.study_days) payload.study_days = goals.study_days;
    if (goals.excluded_subjects) payload.excluded_subjects = goals.excluded_subjects;
    if (goals.denovo_track_subjects) payload.denovo_track_subjects = goals.denovo_track_subjects;
    if (goals.hfr_restricted_subjects) payload.hfr_restricted_subjects = goals.hfr_restricted_subjects;

    await supabase.from('user_goals').upsert(payload);
};


export const saveRuleProgress = async (userId, ruleId, { status, srs_data, keynote, is_flagged }) => {
    const payload = {
        user_id: userId,
        rule_id: ruleId,
        updated_at: new Date().toISOString() // FIX: Enforce ISO string
    };
    if (status !== undefined) payload.status = status;
    if (srs_data !== undefined) payload.srs_data = srs_data;
    if (keynote !== undefined) payload.keynote = keynote;
    if (is_flagged !== undefined) payload.is_flagged = is_flagged;

    await supabase.from('user_progress').upsert(payload);
};


export const saveBulkProgress = async (userId, progressMap) => {
    const rows = Object.entries(progressMap).map(([ruleId, data]) => ({
        user_id: userId,
        rule_id: ruleId,
        status: data.status,
        srs_data: data.srs_data,
        keynote: data.keynote,
        is_flagged: data.is_flagged,
        updated_at: new Date().toISOString() // FIX: Enforce ISO string for bulk updates
    }));
    if (rows.length > 0) {
        await supabase.from('user_progress').upsert(rows);
    }
};


export const saveQuizAttempt = async (userId, { quiz_id, score, user_answer, feedback }) => {
    await supabase.from('user_quiz_attempts').insert({
        user_id: userId,
        quiz_id,
        score,
        user_answer,
        ai_feedback: feedback
    });
};

export const saveDocket = async (userId, docket) => {
    await supabase.from('user_docket').upsert({
        user_id: userId,
        schedule: docket.schedule,
        daily_metrics: docket.daily_metrics || {},
        updated_at: new Date().toISOString() // FIX: Enforce ISO string
    });
};

export const saveUserData = async (userId, fullDataBlob) => {
    try {
        const promises = [];

        // 1. Profile & Meta -> 'users' table
        if (fullDataBlob.profile || fullDataBlob.userMeta || fullDataBlob.diagnostics) {
            const updates = {};
            
            // Handle Profile
            if (fullDataBlob.profile) {
                if (fullDataBlob.profile.name) updates.name = fullDataBlob.profile.name;
                if (fullDataBlob.profile.phone) updates.phone = fullDataBlob.profile.phone;
                if (fullDataBlob.profile.preferences) updates.preferences = fullDataBlob.profile.preferences;
                // GAMIFICATION: Save new fields
                if (fullDataBlob.profile.display_name) updates.display_name = fullDataBlob.profile.display_name;
                if (fullDataBlob.profile.zen_mode !== undefined) updates.zen_mode = fullDataBlob.profile.zen_mode;
            } else {
                // Fallback for flat structure
                if (fullDataBlob.name) updates.name = fullDataBlob.name;
                if (fullDataBlob.phone) updates.phone = fullDataBlob.phone;
            }

            // Handle Meta
            if (fullDataBlob.userMeta) {
                updates.meta = fullDataBlob.userMeta;
            }

            // Handle Diagnostics (Subject Sequence isn't in user_goals, so we store in Preferences)
            if (fullDataBlob.diagnostics && fullDataBlob.diagnostics.subject_sequence) {
                updates.preferences = {
                    ...(updates.preferences || {}),
                    subject_sequence: fullDataBlob.diagnostics.subject_sequence,
                    is_retaker: fullDataBlob.diagnostics.is_retaker, // Optional context
                    prep_course: fullDataBlob.diagnostics.prep_course
                };
            }

            if (Object.keys(updates).length > 0) {
                promises.push(updateUserProfile(userId, updates));
                // If meta is present, we might need a specific call or rely on updateUserProfile handling 'meta' column if added to query
                if (updates.meta) {
                    promises.push(saveUserMeta(userId, updates.meta));
                }
            }
        }

        // 2. Goals -> 'user_goals' table
        if (fullDataBlob.goals) {
            promises.push(updateUserGoals(userId, fullDataBlob.goals));
        }

        // 3. Docket -> 'user_docket' table
        if (fullDataBlob.docket) {
            promises.push(saveDocket(userId, fullDataBlob.docket));
        }

        // 4. Progress -> 'user_progress' table
        if (fullDataBlob.rulesProgress) {
            promises.push(saveBulkProgress(userId, fullDataBlob.rulesProgress));
        }

        await Promise.all(promises);
        
    } catch (error) {
        console.error('Dispatcher Error saving user data:', error);
    }
};

export const getDueCards = async (userId) => {
    const { data, error } = await supabase
        .from('user_progress')
        .select('rule_id')
        .eq('user_id', userId)
        .lt('srs_data->>nextDueDate', new Date().toISOString());
    
    if (error) {
        console.error("Error fetching due cards:", error);
        return [];
    }
    return data.map(r => r.rule_id);
};

export const getUserRating = async (userId, itemId) => {
    const { data } = await supabase.from('content_ratings').select('rating_value').eq('user_id', userId).eq('item_id', itemId).maybeSingle();
    return data ? data.rating_value : null;
};

export const saveUserRating = async (userId, itemId, itemType, ratingValue) => {
    await supabase.from('content_ratings').upsert({ 
        user_id: userId, 
        item_id: itemId, 
        item_type: itemType, 
        rating_value: ratingValue, 
        updated_at: new Date().toISOString() // FIX: Enforce ISO string
    }, { onConflict: 'user_id, item_id' });
};


export const deleteUserRating = async (userId, itemId) => {
    await supabase.from('content_ratings').delete().eq('user_id', userId).eq('item_id', itemId);
};

export const updateOnboardingStatus = async (userId, status) => {
    await supabase.from('users').update({ onboarding_status: status }).eq('id', userId);
};

export const updateUserName = async (userId, name) => {
    await updateUserProfile(userId, { name });
};

// --- EDGE FUNCTION INTEGRATION ---
export const rateCardViaEdge = async (ruleId, rating) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error("No active session");

    const { data, error } = await supabase.functions.invoke('rate-card', {
        body: { rule_id: ruleId, rating: rating },
        headers: {
            Authorization: `Bearer ${session.access_token}`
        }
    });

    if (error) {
        console.error("Edge Function Error:", error);
        throw error;
    }

    return data;
};

export const logActivity = async (userId, actionType, entityId, value, metadata = {}) => {
    try {
        // Fire and forget - don't await this in the main UI thread to keep the app fast
        supabase.from('user_activity_log').insert({
            user_id: userId,
            action_type: actionType,
            entity_id: entityId,
            value: value, // e.g. 1 (Again) to 4 (Easy), or Quiz Score
            metadata: metadata // e.g. { subject: 'torts', is_hfr: true }
        }).then(({ error }) => {
            if (error) console.warn("Gamification Log Error:", error);
        });
    } catch (e) {
        console.warn("Gamification Log Exception:", e);
    }
};

// --- GAMIFICATION: ATOMIC POINTS ---
export const addPoints = async (userId, points) => {
    try {
        const { error } = await supabase.rpc('increment_weekly_points', { 
            x: points, 
            u_id: userId 
        });
        if (error) console.error("Points Error:", error);
    } catch (e) {
        console.error("RPC Error:", e);
    }
};

// --- CRAM DECK OPERATIONS ---

export const createCramDeck = async (userId, deckName, ruleIds) => {
    const { data, error } = await supabase
        .from('user_cram_decks')
        .insert([{ user_id: userId, deck_name: deckName, rule_ids: ruleIds }])
        .select()
        .single();
    if (error) console.error('Error creating cram deck:', error);
    return data;
};

export const updateCramDeckProgress = async (deckId, progressData, lastStudied) => {
    const payload = { progress: progressData };
    if (lastStudied) {
        payload.last_studied_at = new Date().toISOString();
    }
    const { error } = await supabase
        .from('user_cram_decks')
        .update(payload)
        .eq('id', deckId);
    if (error) console.error('Error updating cram deck progress:', error);
};

export const renameCramDeck = async (deckId, newName) => {
    const { error } = await supabase
        .from('user_cram_decks')
        .update({ deck_name: newName })
        .eq('id', deckId);
    if (error) console.error('Error renaming cram deck:', error);
};

export const resetCramDeck = async (deckId) => {
    const { error } = await supabase
        .from('user_cram_decks')
        .update({ progress: {}, last_studied_at: null })
        .eq('id', deckId);
    if (error) console.error('Error resetting cram deck:', error);
};

export const deleteCramDeck = async (deckId) => {
    const { error } = await supabase
        .from('user_cram_decks')
        .delete()
        .eq('id', deckId);
    if (error) console.error('Error deleting cram deck:', error);
};

// --- ARENA / GAMIFICATION ---

export const joinGroup = async (userId, inviteCode) => {
    // 1. Find Group ID from Code
    const { data: group, error: groupError } = await supabase
        .from('groups')
        .select('id, name')
        .eq('invite_code', inviteCode)
        .single();

    if (groupError || !group) throw new Error("Invalid Invite Code");

    // 2. Insert Member
    const { error: joinError } = await supabase
        .from('group_members')
        .insert([{ group_id: group.id, user_id: userId }]);

    if (joinError) {
        if (joinError.code === '23505') throw new Error("You are already in this group.");
        throw joinError;
    }

    return group;
};

/* src/storageService.js */
export const getArenaData = async (userId) => {
    const { data: membership } = await supabase
        .from('group_members')
        .select('group_id')
        .eq('user_id', userId)
        .maybeSingle();

    if (!membership) return { status: 'no_group' };

    const [groupRes, membersRes, winnerRes] = await Promise.all([
        supabase.from('groups').select('*').eq('id', membership.group_id).single(),
        supabase.from('group_members').select(`
            joined_at,
            users (
                id, display_name, weekly_points, zen_mode,
                user_stats!left (current_streak),
                user_achievements (achievement_slug)
            )
        `).eq('group_id', membership.group_id),
        supabase.from('group_hall_of_fame').select(`
            points_won, week_ending,
            users (display_name)
        `).eq('group_id', membership.group_id).order('week_ending', { ascending: false }).limit(1).maybeSingle()
    ]);

    if (membersRes.error) {
        console.error("Leaderboard Error:", membersRes.error);
        return { status: 'error' };
    }

    const leaderboard = membersRes.data.map(m => {
        const u = m.users;
        let streakVal = 0;
        if (u.user_stats) {
            streakVal = Array.isArray(u.user_stats) ? (u.user_stats[0]?.current_streak || 0) : (u.user_stats.current_streak || 0);
        }
        
        return {
            id: u.id,
            name: u.display_name || 'Anonymous',
            points: u.weekly_points || 0,
            streak: streakVal,
            badges: Array.isArray(u.user_achievements) ? u.user_achievements.length : 0,
            isZen: u.zen_mode,
            joinedAt: m.joined_at
        };
    });

    return { 
        status: 'active',
        group: groupRes.data,
        leaderboard,
        lastWinner: winnerRes.data ? {
            name: winnerRes.data.users.display_name,
            points: winnerRes.data.points_won,
            date: winnerRes.data.week_ending
        } : null
    };
};

// --- NOTIFICATIONS & ACHIEVEMENTS ---

// --- NOTIFICATIONS SYSTEM ---

export const sendNotification = async (userId, type, title, message) => {
    // 1. Database Insert (The Source of Truth)
    const { error } = await supabase.from('user_notifications').insert({
        user_id: userId,
        type,    // 'badge', 'system', 'streak', 'arena'
        title,
        message,
        is_read: false
    });
    
    if (error) console.error("Notification Dispatch Error:", error);

    // 2. Future iOS Hook: 
    // This is where we would call: sendPushToDevice(userId, title, message);
};

export const getNotifications = async (userId) => {
    // Fetch last 50 items regardless of read status for the "Journal" view
    const { data, error } = await supabase
        .from('user_notifications')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(50);
    
    if (error) return [];
    return data;
};

export const markNotificationRead = async (notifId) => {
    // We update DB but don't delete, preserving history
    await supabase
        .from('user_notifications')
        .update({ is_read: true })
        .eq('id', notifId);
};

export const markAllNotificationsRead = async (userId) => {
    await supabase
        .from('user_notifications')
        .update({ is_read: true })
        .eq('user_id', userId)
        .eq('is_read', false);
};

// THE HNU PARSER: Checks if a chapter was completed
export const checkAndUnlockChapterAchievement = async (userId, rule, allItemsMap, rulesProgress) => {
    if (!rule || !rule.HNU) return;

    const hnuParts = rule.HNU.split('.');
    const chapterIndex = parseInt(hnuParts[0], 10);
    const subject = rule.subject;

    if (isNaN(chapterIndex)) return;

    const siblings = [];
    for (const item of allItemsMap.values()) {
        if (item.itemType === 'rule' && item.subject === subject && item.HNU) {
            const itemMajor = parseInt(item.HNU.split('.')[0], 10);
            if (itemMajor === chapterIndex) {
                siblings.push(item);
            }
        }
    }

    if (siblings.length === 0) return;

    const allMastered = siblings.every(sibling => {
        const progress = rulesProgress[sibling.id];
        if (!progress) return false;
        if (progress.status === 'mastered') return true;
        const srs = progress.srs_data;
        if (!srs) return false;
        return srs.learningStepIndex === -1 && (srs.currentInterval || 0) >= 21; 
    });

    if (allMastered) {
        const achievementId = `${subject}_${chapterIndex}`;
        const { data: existing } = await supabase
            .from('user_achievements')
            .select('achievement_slug')
            .eq('user_id', userId)
            .eq('achievement_slug', achievementId)
            .maybeSingle();

        if (!existing) {
            await supabase.from('user_achievements').insert({
                user_id: userId,
                achievement_slug: achievementId
            });

            await supabase.from('user_notifications').insert({
                user_id: userId,
                type: 'badge',
                title: 'Badge Unlocked',
                message: `You have mastered all rules in ${subject.toUpperCase()} Chapter ${chapterIndex}.`
            });
            return { unlocked: true, name: `Chapter ${chapterIndex} Mastery` };
        }
    }
    return null;
};

export const syncStreakToDb = async (userId, streakCount) => {
    // 1. Save to DB (Existing)
    await supabase
        .from('user_stats')
        .upsert({ 
            user_id: userId, 
            current_streak: streakCount,
            updated_at: new Date().toISOString()
        }, { onConflict: 'user_id' });

    // 2. NEW: Check for Milestones
    const milestones = [3, 7, 14, 30, 50, 100];
    if (milestones.includes(streakCount)) {
        // Check if we already notified for this streak today to avoid dupe on refresh
        const key = `streak_notif_${userId}_${streakCount}`;
        const hasNotified = localStorage.getItem(key);
        
        if (!hasNotified) {
            await sendNotification(
                userId,
                'streak',
                `${streakCount} Day Streak!`,
                `You are on fire. Consistency is the key to passing the bar.`
            );
            localStorage.setItem(key, 'true');
        }
    }
};
